/**
 * `<video>` binding for remote tracks.
 *
 * A tile can mount before its track is subscribed (the plan is applied
 * asynchronously), so requests are parked and flushed on `TrackSubscribed`.
 * Attaching also hands the element to LiveKit's adaptive stream, which is what
 * lets it pause a tile that scrolls out of view.
 */
import { Track, type RemoteTrack, type Room } from 'livekit-client';
import type { VideoSource } from './contract';

export interface AttachmentRegistry {
  request(room: Room | null, participantId: string, source: VideoSource, el: HTMLVideoElement): () => void;
  /** Call on TrackSubscribed — binds anything that was waiting for this track. */
  flush(participantId: string, track: RemoteTrack): void;
  clear(): void;
}

const SOURCE_FOR: Readonly<Record<VideoSource, Track.Source>> = {
  camera: Track.Source.Camera,
  screen: Track.Source.ScreenShare,
};

const keyOf = (participantId: string, source: VideoSource): string => `${participantId}:${source}`;

const sourceOf = (track: RemoteTrack): VideoSource | null => {
  if (track.source === Track.Source.Camera) return 'camera';
  if (track.source === Track.Source.ScreenShare) return 'screen';
  return null;
};

export function createAttachmentRegistry(): AttachmentRegistry {
  /**
   * Elements stay registered for as long as the tile is mounted, not just until
   * the first attach: a tile can survive an unsubscribe/resubscribe cycle (the
   * plan drops it and picks it up again) and must be re-bound to the new track.
   */
  const bound = new Map<string, Set<HTMLVideoElement>>();

  const trackFor = (room: Room | null, participantId: string, source: VideoSource) =>
    room?.remoteParticipants.get(participantId)?.getTrackPublication(SOURCE_FOR[source])?.track;

  return {
    request(room, participantId, source, el) {
      const key = keyOf(participantId, source);
      const elements = bound.get(key) ?? new Set<HTMLVideoElement>();
      elements.add(el);
      bound.set(key, elements);
      trackFor(room, participantId, source)?.attach(el);

      return () => {
        trackFor(room, participantId, source)?.detach(el);
        const current = bound.get(key);
        if (!current) return;
        current.delete(el);
        if (current.size === 0) bound.delete(key);
      };
    },

    flush(participantId, track) {
      const source = sourceOf(track);
      if (source === null) return;
      for (const el of bound.get(keyOf(participantId, source)) ?? []) track.attach(el);
    },

    clear() {
      bound.clear();
    },
  };
}
